import { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
// Worker pdf.js résolu comme URL par Vite
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import AiGeneralBody from './AiGeneralBody';
import ChatBody from './ChatBody';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type PdfAnnotation = {
  id: number;
  type: 'texte' | 'surlignage' | 'vocal' | 'cercle' | '3d';
  content: string | null;
  voice_key: string | null;
  page: number | null;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  user_name: string;
  created_at: string;
};

type Mode = 'view' | 'comment' | 'highlight' | 'voice';

interface Props {
  fileUrl: string;
  annotations: PdfAnnotation[];
  canEdit?: boolean;
  onAddText: (a: { page: number; x: number; y: number; content: string }) => Promise<void> | void;
  onAddHighlight: (a: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    content?: string;
  }) => Promise<void> | void;
  onAddVoice: (a: {
    page: number;
    x: number;
    y: number;
    audio: Blob;
  }) => Promise<void> | void;
  onDelete?: (annotationId: number) => Promise<void> | void;
  /** Rendu avant les modes (haut-gauche de la toolbar) — ex: Retour au projet */
  toolbarLeading?: React.ReactNode;
  /** Actions au-dessus à droite de la toolbar (+Version, Valider, À revoir, Masquer panneau) */
  toolbarExtras?: React.ReactNode;
  /** Contenu du volet de droite en fullscreen (Infos/Comm/Versions/Assistant contextuel) */
  renderSidePanel?: () => React.ReactNode;
}

type RenderedPage = {
  pageNumber: number;
  viewport: { width: number; height: number };
};

type FsSlot = null | 'side' | 'ai-general' | 'chat';

export default function PdfViewer({
  fileUrl,
  annotations,
  canEdit = true,
  onAddText,
  onAddHighlight,
  onAddVoice,
  onDelete,
  toolbarLeading,
  toolbarExtras,
  renderSidePanel,
}: Props) {
  const [fsSlot, setFsSlot] = useState<FsSlot>(null);
  const toggleSlot = (next: Exclude<FsSlot, null>) =>
    setFsSlot((cur) => (cur === next ? null : next));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [scale, setScale] = useState(1.3);
  const [mode, setMode] = useState<Mode>('view');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // État en cours de capture
  const [pendingText, setPendingText] = useState<{
    page: number;
    x: number;
    y: number;
  } | null>(null);
  const [textDraft, setTextDraft] = useState('');
  const [drawing, setDrawing] = useState<{
    page: number;
    startX: number;
    startY: number;
    curX: number;
    curY: number;
  } | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<{
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [highlightNote, setHighlightNote] = useState('');
  const [recording, setRecording] = useState<{
    page: number;
    x: number;
    y: number;
    recorder: MediaRecorder;
    chunks: Blob[];
  } | null>(null);

  // Popup d'une annotation existante
  const [openAnn, setOpenAnn] = useState<PdfAnnotation | null>(null);

  // ---- Rendu PDF ----
  useEffect(() => {
    let cancelled = false;
    const container = pagesContainerRef.current;
    if (!container) return;

    setLoading(true);
    setError(null);
    container.innerHTML = '';
    setPages([]);

    (async () => {
      try {
        const task = pdfjs.getDocument({
          url: fileUrl,
          withCredentials: true,
        });
        const pdf = await task.promise;
        if (cancelled) return;
        const rendered: RenderedPage[] = [];
        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) break;
          const page = await pdf.getPage(n);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = 'block';
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          const pageEl = document.createElement('div');
          pageEl.className = 'pdf-page';
          pageEl.dataset.pageNumber = String(n);
          pageEl.style.width = `${viewport.width}px`;
          pageEl.style.height = `${viewport.height}px`;
          pageEl.appendChild(canvas);
          container.appendChild(pageEl);

          await page.render({ canvasContext: ctx, viewport }).promise;
          rendered.push({ pageNumber: n, viewport: { width: viewport.width, height: viewport.height } });
        }
        if (!cancelled) {
          setPages(rendered);
          setLoading(false);
        }
      } catch (e) {
        console.error('PDF error', e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Impossible de charger le PDF.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl, scale]);

  // ---- Fullscreen ----
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  // ---- Calcul coordonnées % depuis un event ----
  function getPct(e: React.PointerEvent<HTMLDivElement>, pageEl: HTMLDivElement) {
    const rect = pageEl.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  }

  // ---- Handlers overlay ----
  function onOverlayPointerDown(e: React.PointerEvent<HTMLDivElement>, page: number) {
    if (!canEdit || mode === 'view') return;
    const pageEl = e.currentTarget.parentElement as HTMLDivElement;
    const { x, y } = getPct(e, pageEl);

    if (mode === 'comment') {
      setPendingText({ page, x, y });
      setTextDraft('');
    } else if (mode === 'highlight') {
      setDrawing({ page, startX: x, startY: y, curX: x, curY: y });
    } else if (mode === 'voice') {
      startRecording(page, x, y);
    }
  }

  function onOverlayPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drawing) return;
    const pageEl = e.currentTarget.parentElement as HTMLDivElement;
    const { x, y } = getPct(e, pageEl);
    setDrawing({ ...drawing, curX: x, curY: y });
  }

  function onOverlayPointerUp(_e: React.PointerEvent<HTMLDivElement>) {
    if (!drawing) return;
    const x = Math.min(drawing.startX, drawing.curX);
    const y = Math.min(drawing.startY, drawing.curY);
    const width = Math.abs(drawing.curX - drawing.startX);
    const height = Math.abs(drawing.curY - drawing.startY);
    if (width > 0.005 && height > 0.005) {
      setPendingHighlight({ page: drawing.page, x, y, width, height });
      setHighlightNote('');
    }
    setDrawing(null);
  }

  // ---- Voice recording ----
  async function startRecording(page: number, x: number, y: number) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunks.push(ev.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await onAddVoice({ page, x, y, audio: blob });
        setRecording(null);
      };
      recorder.start();
      setRecording({ page, x, y, recorder, chunks });
    } catch (e) {
      alert(
        "Impossible d'accéder au microphone. Autorise l'accès dans les paramètres du navigateur."
      );
      console.error(e);
    }
  }

  function stopRecording() {
    if (recording) recording.recorder.stop();
  }

  async function submitText() {
    if (!pendingText || !textDraft.trim()) {
      setPendingText(null);
      return;
    }
    await onAddText({
      page: pendingText.page,
      x: pendingText.x,
      y: pendingText.y,
      content: textDraft.trim(),
    });
    setPendingText(null);
    setTextDraft('');
  }

  async function submitHighlight() {
    if (!pendingHighlight) return;
    await onAddHighlight({
      page: pendingHighlight.page,
      x: pendingHighlight.x,
      y: pendingHighlight.y,
      width: pendingHighlight.width,
      height: pendingHighlight.height,
      content: highlightNote.trim() || undefined,
    });
    setPendingHighlight(null);
    setHighlightNote('');
  }

  // ---- Rendu overlays ----
  function renderAnnotationsFor(page: number) {
    return annotations
      .filter((a) => a.page === page && a.x !== null && a.y !== null)
      .map((a) => {
        if (a.type === 'surlignage' && a.width && a.height) {
          return (
            <div
              key={a.id}
              className="ann-highlight"
              style={{
                left: `${(a.x ?? 0) * 100}%`,
                top: `${(a.y ?? 0) * 100}%`,
                width: `${a.width * 100}%`,
                height: `${a.height * 100}%`,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpenAnn(a);
              }}
              title={a.content ?? `Surligné par ${a.user_name}`}
            />
          );
        }
        const icon = a.type === 'vocal' ? '🎤' : '💬';
        return (
          <button
            key={a.id}
            className={`ann-pin ann-${a.type}`}
            style={{ left: `${(a.x ?? 0) * 100}%`, top: `${(a.y ?? 0) * 100}%` }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenAnn(a);
            }}
            title={a.content ?? `${a.user_name}`}
          >
            {icon}
          </button>
        );
      });
  }

  function renderDrawingRect() {
    if (!drawing) return null;
    const x = Math.min(drawing.startX, drawing.curX);
    const y = Math.min(drawing.startY, drawing.curY);
    const w = Math.abs(drawing.curX - drawing.startX);
    const h = Math.abs(drawing.curY - drawing.startY);
    return (
      <div
        className="ann-highlight drawing"
        style={{
          left: `${x * 100}%`,
          top: `${y * 100}%`,
          width: `${w * 100}%`,
          height: `${h * 100}%`,
          pointerEvents: 'none',
        }}
      />
    );
  }

  return (
    <div className="pdf-viewer" ref={wrapperRef}>
      <div className="pdf-toolbar">
        <div className="pdf-modes">
          {toolbarLeading && (
            <>
              {toolbarLeading}
              <span className="pdf-toolbar-sep" />
            </>
          )}
          <button
            onClick={() => setMode('view')}
            className={`pdf-mode-btn ${mode === 'view' ? 'active' : ''}`}
            title="Consulter"
          >
            👁 Consulter
          </button>
          {canEdit && (
            <>
              <button
                onClick={() => setMode('comment')}
                className={`pdf-mode-btn ${mode === 'comment' ? 'active' : ''}`}
                title="Cliquer pour placer un commentaire"
              >
                💬 Commenter
              </button>
              <button
                onClick={() => setMode('highlight')}
                className={`pdf-mode-btn ${mode === 'highlight' ? 'active' : ''}`}
                title="Dessiner un rectangle pour surligner"
              >
                ▭ Surligner
              </button>
              <button
                onClick={() => setMode('voice')}
                className={`pdf-mode-btn ${mode === 'voice' ? 'active' : ''}`}
                title="Cliquer pour démarrer un mémo vocal à cet endroit"
              >
                🎤 Mémo vocal
              </button>
            </>
          )}
        </div>
        <div className="pdf-zoom">
          <button
            className="pdf-mode-btn"
            onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
            title="Zoom −"
          >
            −
          </button>
          <span style={{ minWidth: 54, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            className="pdf-mode-btn"
            onClick={() => setScale((s) => Math.min(3, s + 0.2))}
            title="Zoom +"
          >
            +
          </button>
          <button className="pdf-mode-btn" onClick={toggleFullscreen} title="Plein écran">
            {isFullscreen ? '⇲' : '⤢'}
          </button>
          {toolbarExtras && <span className="pdf-toolbar-sep" />}
          {toolbarExtras}
        </div>
      </div>

      <div className="pdf-pages-scroll">
        {loading && <div style={{ padding: 40 }}>Chargement du PDF…</div>}
        {error && <div className="error-msg" style={{ margin: 20 }}>{error}</div>}
        <div
          ref={pagesContainerRef}
          className="pdf-pages"
          style={{
            // Au moment où le conteneur est peuplé via DOM imperatif, on pose juste le fond
            cursor: mode === 'view' ? 'default' : mode === 'highlight' ? 'crosshair' : 'pointer',
          }}
        />
        {/* Les overlays s'attachent à chaque page rendue via un effet séparé */}
        <PageOverlays
          pages={pages}
          containerRef={pagesContainerRef}
          onPointerDown={onOverlayPointerDown}
          onPointerMove={onOverlayPointerMove}
          onPointerUp={onOverlayPointerUp}
          renderAnnotationsFor={renderAnnotationsFor}
          renderDrawingRect={renderDrawingRect}
          activePage={drawing?.page ?? null}
        />
      </div>

      {/* Popup nouveau commentaire */}
      {pendingText && (
        <div className="modal-backdrop" onClick={() => setPendingText(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="card-meta">
              Commentaire — page {pendingText.page}
            </div>
            <textarea
              rows={4}
              autoFocus
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              placeholder="Écris ton commentaire…"
              style={{
                width: '100%',
                padding: 10,
                marginTop: 8,
                border: '1px solid var(--c-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--c-bg)',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-ghost" onClick={() => setPendingText(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={submitText}>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup finalisation highlight */}
      {pendingHighlight && (
        <div className="modal-backdrop" onClick={() => setPendingHighlight(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="card-meta">
              Surlignage — page {pendingHighlight.page}
            </div>
            <textarea
              rows={3}
              autoFocus
              value={highlightNote}
              onChange={(e) => setHighlightNote(e.target.value)}
              placeholder="Note attachée au surlignage (facultatif)"
              style={{
                width: '100%',
                padding: 10,
                marginTop: 8,
                border: '1px solid var(--c-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--c-bg)',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-ghost" onClick={() => setPendingHighlight(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={submitHighlight}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enregistrement en cours */}
      {recording && (
        <div className="recording-bar">
          <span className="rec-dot" /> Enregistrement en cours (page {recording.page})…
          <button className="btn btn-primary" onClick={stopRecording} style={{ marginLeft: 12 }}>
            Arrêter et enregistrer
          </button>
        </div>
      )}

      {/* Fullscreen : 3 FABs + 1 slot unique (mutual exclusion) */}
      {isFullscreen && (
        <>
          <div className="pdf-fs-fabs">
            <button
              className="fab fab-info"
              title="Volet du document (Infos · Comm · Versions · Assistant)"
              onClick={() => toggleSlot('side')}
              style={{
                background: fsSlot === 'side' ? 'var(--c-sidebar)' : 'var(--c-sidebar)',
                outline: fsSlot === 'side' ? '3px solid #fff' : 'none',
              }}
            >
              ☰
            </button>
            <button
              className="fab fab-ia"
              title="Assistant IA général"
              onClick={() => toggleSlot('ai-general')}
              style={{ outline: fsSlot === 'ai-general' ? '3px solid #fff' : 'none' }}
            >
              ✦
            </button>
            <button
              className="fab fab-chat"
              title="Chat direct"
              onClick={() => toggleSlot('chat')}
              style={{ outline: fsSlot === 'chat' ? '3px solid #fff' : 'none' }}
            >
              💬
            </button>
          </div>

          {fsSlot !== null && (
            <div className="pdf-fs-overlay">
              <div
                className="pdf-fs-overlay-head"
                style={{
                  background:
                    fsSlot === 'side'
                      ? 'var(--c-sidebar)'
                      : fsSlot === 'ai-general'
                        ? 'var(--c-terracotta)'
                        : 'var(--c-sage)',
                }}
              >
                <span>
                  {fsSlot === 'side'
                    ? 'Volet du document'
                    : fsSlot === 'ai-general'
                      ? 'Assistant IA · général'
                      : 'Chat direct'}
                </span>
                <button
                  className="icon-btn"
                  onClick={() => setFsSlot(null)}
                  style={{ color: '#fff' }}
                >
                  ×
                </button>
              </div>
              <div className="pdf-fs-overlay-body">
                {fsSlot === 'side' &&
                  (renderSidePanel ? (
                    renderSidePanel()
                  ) : (
                    <div className="card-meta">Aucun volet à afficher.</div>
                  ))}
                {fsSlot === 'ai-general' && <AiGeneralBody />}
                {fsSlot === 'chat' && <ChatBody />}
              </div>
            </div>
          )}
        </>
      )}

      {/* Détail annotation */}
      {openAnn && (
        <div className="modal-backdrop" onClick={() => setOpenAnn(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="card-meta">
              {openAnn.user_name} · {new Date(openAnn.created_at).toLocaleString('fr-FR')}
            </div>
            <div style={{ margin: '8px 0' }}>
              {openAnn.type === 'vocal' && openAnn.voice_key ? (
                <audio
                  controls
                  src={`/api/v1/files?key=${encodeURIComponent(openAnn.voice_key)}`}
                  style={{ width: '100%' }}
                />
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{openAnn.content || '—'}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              {onDelete && (
                <button
                  className="btn btn-revoir"
                  onClick={async () => {
                    await onDelete(openAnn.id);
                    setOpenAnn(null);
                  }}
                >
                  Supprimer
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => setOpenAnn(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Monte un overlay absolu sur chaque page rendue. pdf.js rend les pages via DOM imperatif
 * dans `pagesContainerRef`, donc on attache nos overlays en post-effect.
 */
function PageOverlays({
  pages,
  containerRef,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  renderAnnotationsFor,
  renderDrawingRect,
  activePage,
}: {
  pages: RenderedPage[];
  containerRef: React.RefObject<HTMLDivElement>;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>, page: number) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  renderAnnotationsFor: (page: number) => React.ReactNode;
  renderDrawingRect: () => React.ReactNode;
  activePage: number | null;
}) {
  // Nettoie et re-crée les overlays à chaque rendu des pages.
  // On utilise des Portals via des divs créés dynamiquement.
  const [overlayRoots, setOverlayRoots] = useState<Record<number, HTMLDivElement>>({});

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const roots: Record<number, HTMLDivElement> = {};
    for (const p of pages) {
      const pageEl = container.querySelector(
        `.pdf-page[data-page-number="${p.pageNumber}"]`
      ) as HTMLDivElement | null;
      if (!pageEl) continue;
      // Retire un éventuel overlay précédent
      pageEl.querySelectorAll('.pdf-overlay-root').forEach((n) => n.remove());
      const root = document.createElement('div');
      root.className = 'pdf-overlay-root';
      pageEl.appendChild(root);
      roots[p.pageNumber] = root;
    }
    setOverlayRoots(roots);
    return () => {
      Object.values(roots).forEach((r) => r.remove());
    };
  }, [pages, containerRef]);

  return (
    <>
      {pages.map((p) => {
        const root = overlayRoots[p.pageNumber];
        if (!root) return null;
        const overlay = (
          <div
            className="pdf-overlay"
            onPointerDown={(e) => onPointerDown(e, p.pageNumber)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={(e) => {
              if (activePage === p.pageNumber) onPointerUp(e);
            }}
          >
            {renderAnnotationsFor(p.pageNumber)}
            {activePage === p.pageNumber && renderDrawingRect()}
          </div>
        );
        return <PortalInto key={p.pageNumber} target={root}>{overlay}</PortalInto>;
      })}
    </>
  );
}

// Minimal portal sans react-dom/createPortal en import direct :
import { createPortal } from 'react-dom';
function PortalInto({ target, children }: { target: Element; children: React.ReactNode }) {
  return createPortal(children, target);
}
